import Webcam from "react-webcam";

import { useRef, useState, useCallback } from "react";
import PropTypes from "prop-types";

import {
  Icon,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Grid,
  Snackbar,
  Alert,
  Button,
  Box,
  Typography,
  InputAdornment,
  IconButton,
} from "@mui/material";

function DocumentField({ displayName, fieldRef, options }) {
  const [fieldError, showFieldError] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);

  const webcamRef = useRef(`${fieldRef}_${displayName}`);
  const [imgSrc, setImgSrc] = useState(null);
  const [capturedImg, setCapturedImg] = useState(null);
  const [cameraUsed, setCameraUsed] = useState(false);
  const [uploadType, setUploadType] = useState(null);

  const [openWebCamDialog, setOpenWebCamDialog] = useState(false);

  const videoConstraints = {
    facingMode: cameraUsed ? "user" : { exact: "environment" },
  };

  // 📸 CAPTURE
  const capture = useCallback(async () => {
    const imageSrc = webcamRef.current.getScreenshot();

    if (!imageSrc) {
      setErrorMsg("No photo image found.");
      showFieldError(true);
      return;
    }

    setImgSrc(imageSrc);
    setCapturedImg(imageSrc);
    setOpenWebCamDialog(false);
  }, [webcamRef]);

  // 📁 BASE64
  async function toBase64(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
    });
  }

  // 📁 FILE HANDLER
  async function handleFileChange(event) {
    const file = event.target.files[0];
    const fileKb = Math.round(file.size / 1024);

    if (fileKb > 5000) {
      setErrorMsg("File size cannot exceed 5MB.");
      showFieldError(true);
      return;
    }

    await toBase64(file)
      .then((res) => {
        if (
          res.startsWith("data:image/heic;base64") ||
          res.includes("data:image/heic;base64")
        ) {
          setErrorMsg(
            "File format not supported. Recommended formats: JPEG, JPG, PNG, PDF",
          );
          showFieldError(true);
          return;
        }

        setImgSrc(res);
        setCapturedImg(res);
        setOpenWebCamDialog(false);
      })
      .catch((err) => {
        setErrorMsg(err);
        showFieldError(true);
      });
  }

  return (
    <Box>
      {/* ERROR */}
      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        autoHideDuration={5000}
        onClose={() => showFieldError(false)}
        open={fieldError}
      >
        <Alert severity="error" sx={{ width: "100%" }}>
          {errorMsg}
        </Alert>
      </Snackbar>

      {/* DIALOG (NOW MATCHES HUMAN FACE) */}
      <Dialog open={openWebCamDialog} fullWidth maxWidth="xs">
        <DialogTitle>
          <Box>
            {uploadType === "scan" ? "Scan document" : "Upload document"}
          </Box>
        </DialogTitle>

        <DialogContent>
          <Box mt={2} mb={3}>
            {uploadType === "scan" ? (
              <Box>
                <Webcam
                  width="100%"
                  ref={webcamRef}
                  screenshotQuality={1}
                  videoConstraints={videoConstraints}
                />

                <Box textAlign="right">
                  <IconButton
                    onClick={() => setCameraUsed(!cameraUsed)}
                    sx={{ mr: 1 }}
                  >
                    <Icon>cameraswitch</Icon>
                  </IconButton>

                  <IconButton onClick={capture}>
                    <Icon>photo_camera</Icon>
                  </IconButton>
                </Box>
              </Box>
            ) : (
              <TextField
                fullWidth
                type="file"
                onChange={handleFileChange}
                inputProps={{
                  accept: "application/pdf,image/*,application/msword",
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Icon>article</Icon>
                    </InputAdornment>
                  ),
                }}
              />
            )}
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setOpenWebCamDialog(false)}>
            <Icon>close</Icon>
          </Button>
        </DialogActions>
      </Dialog>

      {/* HEADER */}
      <Box sx={{ minWidth: 120, width: "100%" }}>
        <Grid container spacing={3}>
          <Grid item xs={6}>
            <Typography variant="h6" fontWeight="light" fontSize="small" mb={2}>
              {displayName}
            </Typography>
          </Grid>

          <Grid item xs={6}>
            <Box textAlign="right">
              <IconButton
                onClick={() => {
                  setUploadType("scan");
                  setImgSrc(null);
                  setCapturedImg(null);
                  setOpenWebCamDialog(true);
                }}
              >
                <Icon>camera_alt</Icon>
              </IconButton>

              <IconButton
                onClick={() => {
                  setUploadType("file");
                  setImgSrc(null);
                  setCapturedImg(null);
                  setOpenWebCamDialog(true);
                }}
              >
                <Icon>upload_file</Icon>
              </IconButton>
            </Box>
          </Grid>
        </Grid>

        {/* ✅ SAME CAPTURE BOX */}
        <Box
          sx={{
            border: "2px dashed #d2d6da",
            borderRadius: "12px",
            height: 220,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            position: "relative",
            overflow: "hidden",
            backgroundColor: "#f8f9fa",
            mt: 2,
          }}
        >
          {!imgSrc && (
            <Box textAlign="center">
              <Box display="flex" justifyContent="center" gap={2}>
                <IconButton
                  onClick={() => {
                    setUploadType("scan");
                    setOpenWebCamDialog(true);
                  }}
                  sx={{ width: 64, height: 64, backgroundColor: "#e3f2fd" }}
                >
                  <Icon fontSize="large">photo_camera</Icon>
                </IconButton>

                <IconButton
                  onClick={() => {
                    setUploadType("file");
                    setOpenWebCamDialog(true);
                  }}
                  sx={{ width: 64, height: 64, backgroundColor: "#ede7f6" }}
                >
                  <Icon fontSize="large">upload</Icon>
                </IconButton>
              </Box>

              <Typography variant="caption" mt={1} display="block">
                Take a photo or upload file
              </Typography>
            </Box>
          )}

          {imgSrc && (
            <>
              <img
                src={imgSrc}
                alt="document"
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                }}
              />

              <IconButton
                onClick={() => {
                  setImgSrc(null);
                  setCapturedImg(null);
                }}
                sx={{
                  position: "absolute",
                  top: 10,
                  right: 10,
                  backgroundColor: "white",
                  border: "1px solid rgba(0,0,0,0.2)",
                  boxShadow: 3,
                }}
              >
                <Icon color="error">close</Icon>
              </IconButton>
            </>
          )}
        </Box>

        <input
          id={`dff_${fieldRef}`}
          type="hidden"
          name={fieldRef}
          value={capturedImg}
        />
      </Box>
    </Box>
  );
}

DocumentField.propTypes = {
  fieldRef: PropTypes.string.isRequired,
  displayName: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(PropTypes.any),
};

DocumentField.defaultProps = {
  options: null,
};

export default DocumentField;
